import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { docBaseUrl as pdfBaseUrl } from "../levelContentData.js";
import { readerDocsData} from "../readerData.js";
import { Worker, Viewer } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import { slugToText, slugify } from "../../../utils/slugify";
import { useTranslation } from "../../../utils/hooks/translateHook";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import { fullScreenPlugin } from '@react-pdf-viewer/full-screen';
import "../ReaderHome/ReaderHome.css";
import download from '../styles/icon/download.svg';
import videoArrow from "../../../images/icons/videoArrow.svg";
import downloadIcon from "../../../images/icons/download.svg";
import viewIcon from "../../../images/icons/viewIcon.svg";

export const API_URL = window.location.hostname === "localhost"
  ?  "http://localhost:4000/api"
  : process.env.REACT_APP_API_URL



const useJumpPlugin = () => {
  const jumpRef = useRef(null);
  return {
    jumpToRef: jumpRef,
    plugin: {
      install: (pluginFunctuions) => { jumpRef.current = pluginFunctuions.jumpToPage; },
      uninstall: () => { jumpRef.current = null; },
    },
  };
};

const ReaderHome = ({ user }) => {
  const { courseSlug, pdfSlug } = useParams();
  const pdfUrl = `${pdfBaseUrl}${pdfSlug}.pdf`;
  const navigate = useNavigate();
  const t = useTranslation();
  const [sectionSlug, setSectionSlug] = useState(null);
  const [pdfReady, setPdfReady] = useState(false);
  const [note, setNote] = useState("");
  const [openBook, setOpenBook] = useState(null);
  const [bookmarks, setBookmarks] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [searchParams] = useSearchParams(); 
  const pageFromURL = useMemo(() => searchParams.get("page"), [searchParams]);
  const location = useLocation();
  
  const sectionFromURL = useMemo(() => searchParams.get("section"), [searchParams]);
    const currentDoc = useMemo(() => {
      return readerDocsData.find(
        (doc) => slugify(doc.slug) === slugify(pdfSlug)
      );
    }, [courseSlug, pdfSlug]);
  
  const noteSlug = useMemo(() => {
    if (!pdfSlug || !sectionSlug) return null;
    return `${slugify(pdfSlug)}-${slugify(sectionSlug)}`;
  },[pdfSlug, sectionSlug]);

  const selectedPdf =
    readerDocsData.find(doc => doc.slug === pdfSlug) ||
    readerDocsData[0];

  useEffect(() => {
    if (!location.pathname.startsWith("/docs")) {
      setSectionSlug(null);
      setOpenBook(null);
      setPdfReady(false);
      setCurrentPage(0);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!currentDoc) return;

    if (sectionSlug === null) {
      if (sectionFromURL) {
        setSectionSlug(sectionFromURL);
        setOpenBook(pdfSlug);
      } else {
        setSectionSlug(currentDoc.miniTitles[0]?.slug);
      }
    }
  }, [currentDoc, sectionFromURL]);

  const { jumpToRef, plugin: jumpPlugin } = useJumpPlugin();
  const fullScreenPluginInstance = fullScreenPlugin();

  const defaultPluginInstance = defaultLayoutPlugin({
    sidebarTabs: () => [],
    renderToolbar: (Toolbar) => (
      <Toolbar>
        {(slots) => (
          <div className = "pdf-toolbar-wrapper">
            {slots.GoToPreviousPage && slots.GoToPreviousPage()}
            {slots.CurrentPageInput && slots.CurrentPageInput()}
            {slots.GoToNextPage && slots.GoToNextPage()}
            {slots.ZoomOut && slots.ZoomOut()}
            {slots.Zoom && slots.Zoom()}
            {slots.ZoomIn && slots.ZoomIn()}
            {slots.Print && slots.Print()}
            <fullScreenPluginInstance.EnterFullScreenButton />
          </div>
        )}
      </Toolbar>
    ),
  });

  const handleAddBookmark = () => {
    if (!bookmarks.includes(currentPage)) {
      const updated = [...bookmarks, currentPage].slice(-5); //change the bookmarker count here
      setBookmarks(updated);

      fetch(`${API_URL}/bookmarks/${courseSlug}/${pdfSlug}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ pages: updated }),
      }).catch((err) => console.error("Failed to save bookmarks", err));
    }
  };

  const jumpTo = (pageIndex) => {
    if (jumpToRef.current) {
      jumpToRef.current(pageIndex);
    } else {
      console.warn("jumpToPage not available yet");
    }
  };

  useEffect(() => {
    if (!pdfSlug) return;

    console.log("Fetching bookmarks for:", pdfSlug);

    fetch(`${API_URL}/bookmarks/${courseSlug}/${pdfSlug}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        console.log("BOOKMARKS RESPONSE:", data);
        setBookmarks(data?.pages || []);
      })
      .catch((err) => {
        console.error("Failed to load bookmarks", err);
        setBookmarks([]);
      });
  }, [pdfSlug]);
  /*Bookmarks piece end */

 
/*   useEffect(() => {
    if (!pdfSlug || !sectionSlug) return;

    const lecture = slugify(pdfSlug);
    const section = slugify(sectionSlug)
    console.log("FETCH NOTES:", lecture, section);
     
    fetch(`${API_URL}/notes/${courseSlug}/${noteSlug}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
          console.log("NOTES RESPONSE:", data);
        setNote(data?.content || "")
      })
      .catch(() => setNote(""));
  }, [pdfSlug, sectionSlug]); */

  useEffect(() => {
    if ( window.innerWidth < 1340 && !pdfSlug ) {
      navigate( `/course/${courseSlug}/ebooksTree`,
        { replace: true }
      );
    }
  }, [courseSlug, pdfSlug, navigate]);

  useEffect(() => {
    if (!courseSlug || !noteSlug) return;
    console.log("FETCH NOTES:", courseSlug, noteSlug);
    fetch (`${API_URL}/notes/${courseSlug}/${noteSlug}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    })
    .then((res) => res.json())
    .then((data) => {
      console.log("NOTES RESPONSE:", data);
      setNote(data?.content || "");
    })
    .catch(() => setNote(""));
  }, [courseSlug, noteSlug]);


  useEffect(() => {
    if (!pdfReady) return;

    if (pageFromURL) {
      const interval = setInterval(() => {
        if (jumpToRef.current) {
          jumpTo(Number(pageFromURL));
          clearInterval(interval);
        }
      }, 200);
    }
  }, [pdfReady, pageFromURL]);

  const handleSaveNote = () => {
    if (!courseSlug || !noteSlug) return;
    
    const now = new Date().toLocaleDateString("en-GB");
    const content = `${note} - ${now}`;

    fetch(`${API_URL}/notes/${courseSlug}/${noteSlug}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
      body: JSON.stringify({ content })
    }).then(() => alert("Note saved!"));
  };
 

  const handleDownloadNote = () => {
    const element = document.createElement("a");
    const file = new Blob([note], { type: "text/plain"});
    element.href = URL.createObjectURL(file);
    element.download = `${slugToText(pdfSlug)}-note.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  
  return (
    <div className="layout">
      <div className="doc-page">
       <div className="doc-content-fundamentals">
      
        <div className="books-sidebar-html">
          <h4 className="title-sidebar">
            <span>{t("ebooks")}</span> 
            <img src={videoArrow} alt="" className="video-sidebar-arrow"/>
          </h4>
          <aside className={`sidebar ${ openBook ? "expanded" : "collapsed" }`}>
            <div className="ebook-list-box" >
              {readerDocsData.map(doc => (
                <div key={doc.slug} className="ebook-item">
                  <button className={`sidetitle ${openBook === doc.slug ? "active" : ""}`}
                    onClick={() => { setOpenBook(openBook === doc.slug ? null : doc.slug)
                            if (doc.slug !== pdfSlug) navigate(`/course/${courseSlug}/docs/${doc.slug}`);
                    }}>
                    {doc.title}
                  </button>
                  {openBook === doc.slug && doc.miniTitles && doc.miniTitles.length > 0 && (
                    <div className="ebooklist-open_html" >
                      {doc.miniTitles.map((mini, idx) => (
                        <button key={mini.title}
                                onClick={() => { jumpTo(mini.page); setSectionSlug(mini.slug); }}
                                className={`button sidebutton ${sectionSlug === mini.slug ? "active" : ""}`}> 
                          {mini.title}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </aside>
        </div>

        {!pdfSlug ? (
          <div className="landing-only-wrapper">
            <div className="landingImageDiv">
              <div className="landingImage-containerbubble">
                <div className="landingImage-bubble">
                  {t('ebookBubbleHtml')}
                </div>
              </div>
            </div>
          </div>
          ) : (
            <div className="frameB ebooks-frame">
              <div className="ebooks-mobile-bar">
                <span className="ebooks-mobile-link"
                  onClick={() => navigate(`/course/${courseSlug}/ebooksTree`)}>
                  {t("ebooks")}
                </span>
                <span className="ebooks-mobile-divider">|</span>
                {currentDoc && (
                  <span className="ebooks-mobile-current">
                    {currentDoc.title.toUpperCase()}
                  </span>
                )}
              </div>
              <div className="frame-header-html">
                <span className="frame-chapter-html">{currentDoc?.title?.toUpperCase()}</span>
              </div>
              <div className="frame__inner_html ebooks-inner">
              <div className="doc-page">
                <div className="doc-content">
                  <main className="doc-main">
                    <div className="doc-box">
                      <div className="pdf-wrapper">
                        <div className="mark">
                          <button className="add-bookmark-button" onClick={handleAddBookmark}> 🔖 {t('addBookmark')} </button>
                          <ul className="bookmark-buttons">
                            {!pdfReady ? (
                              <li style={{ opacity: 0.5 }}>📄 {t('loadingBookmarks')}</li>
                            ) : bookmarks.length > 0 ? (
                              bookmarks.map((page, idx) => (
                                <li key={`bookmark-${page}`}><button onClick={() => jumpTo(page)}>Page {page + 1}</button></li>
                              ))
                            ) : (
                              <li style={{ opacity: 0.5 }}>📭 {t('noBookmarks')}</li>
                            )}
                          </ul>
                        </div>
                        <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                          <Viewer
                            fileUrl={pdfUrl}
                            plugins={[defaultPluginInstance, jumpPlugin, fullScreenPluginInstance]}
                            onPageChange={(e) => setCurrentPage(e.currentPage)}
                            onDocumentLoad={() => {setPdfReady(true);
                              const page = searchParams.get("page");
                              if (page && jumpToRef.current) {
                                setTimeout(() => jumpTo(Number(page)), 200);
                              }
                            }}
                          />
                        </Worker>
                      </div>
                    </div>
                  </main>  

                  <aside className="notes-panel">
                    <div className="notes-section-h">
                      <div className="notes-header-h"> 
                        <p> 
                          <strong>{currentDoc?.title?.toUpperCase()}</strong>
                          {sectionSlug && ( <> <strong> | {slugToText(sectionSlug)}</strong> </>)}
                        </p>
                        <div className="note-layout">
                          <div className="note-buttons-create">
                            <button className="icon-button-notes-html" onClick={handleSaveNote} title="Save">
                              <div className="icon"style={{ "--icon": `url(${viewIcon})` }}/>
                            </button>
                            <button className="icon-button-notes-html" onClick={handleDownloadNote} title="Download">
                              <div className="icon" style={{ "--icon": `url(${downloadIcon})` }}/>
                            </button>
                          </div>
                        </div>
                      </div>
                      <textarea className="note-input"  
                        placeholder="Write your notes here..."
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                      />
                    </div>       
                  </aside>

                </div>
                </div>
              </div>
            </div>
        )}
      </div>
    </div>
  </div>
  )
}


export default ReaderHome;